/**
 * Session presence (Tier 0 #3) — heartbeat + signed-in-elsewhere warning.
 * The cache-first data layer is single-writer: two concurrent sessions on one
 * account risk stale-cache stomps. Until real multi-seat exists, we warn.
 * One row per tab/device in user_sessions (RLS: own rows), refreshed every
 * 60s; if another session beat within the last 2 minutes, toast once (re-arms
 * if the other session goes away and comes back).
 */
import { sb, CLOUD_USER } from "./supabase";
import { toast } from "./dom";

const SKEY = "enigma_session_id";

function sessionId(): string {
  let id = sessionStorage.getItem(SKEY);
  if (!id) {
    id = (crypto && "randomUUID" in crypto) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SKEY, id);
  }
  return id;
}

let _warned = false;

async function beat(): Promise<void> {
  if (!CLOUD_USER) return;
  const sid = sessionId();
  try {
    await sb.from("user_sessions").upsert({
      user_id: CLOUD_USER.id,
      session_id: sid,
      last_seen: new Date().toISOString(),
      ua: (navigator.userAgent || "").slice(0, 120),
    }, { onConflict: "user_id,session_id" });

    const cutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
    const { data } = await sb.from("user_sessions")
      .select("session_id")
      .neq("session_id", sid)
      .gte("last_seen", cutoff);
    const others = data?.length || 0;
    if (others > 0 && !_warned) {
      _warned = true;
      toast("Heads up: this account is active in another tab or device — avoid editing the same records in both places", "err");
    } else if (others === 0) {
      _warned = false; // re-arm for the next concurrent session
    }

    // Opportunistic cleanup of day-old heartbeat rows.
    void sb.from("user_sessions").delete()
      .lt("last_seen", new Date(Date.now() - 24 * 3600 * 1000).toISOString());
  } catch { /* offline — nothing to do */ }
}

export function startPresence(): void {
  void beat();
  setInterval(() => { void beat(); }, 60_000);
}
